// REGISTER DEVICE (UI rebuild batch 4 · custody setup · reference build) — Astra's sequence, Connect → verify device →
// prove key, with only the current step's controls shown. Key purpose names the Nano that holds it (the operator, 22 Oct
// 2026): deposits, Hot and Merchant Hot on the desk operator's Nano, Treasury on the owner's.
//
// THE REGISTRATION IS TODAY'S, COPIED (screens/custody/Keys.tsx RegisterKey): the first account with no active key is
// pre-selected; exportAccountKey over the cable (the Nano SHOWS the account's first address to compare); for accounts
// 2–4 the desk's challenge and a 0 TRX transfer from the registered device to that first address, expired before it was
// made, signed on the Nano (no blind signing, no message signing — the operator, 22 Oct); registerKey with the proof;
// an account already holding THIS key adopts it; rememberAccountKey keeps the public parts so this browser can check
// every address the key issues; the refusals by reason. Registration is by cable — the one place cable stays; payouts
// are signed by phone over Bluetooth.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { custody as api, type CustodyKey } from '../../../lib/api';
import { ApiError } from '../../../lib/errors';
import { CLASS_WORD } from '../../../lib/custody-format';
import { KEY_ACCOUNTS, KEY_ACCOUNT_CLASS } from '../../../lib/custody-renewals';
import { webHid, exportAccountKey, signOnLedger, buildNetwork, type LedgerStep } from '../../../lib/ledger-cable';
import { buildKeyProof } from '../../../../../signer/src/key-approval.mjs';
import { rememberAccountKey } from '../../../lib/custody-keys-local';
import { keyRefusal } from '../../custody/refusals';
import { Field } from '../../../components/Field';
import { StateLine } from '../../../components/v2/kit';
import { FormFrame, FormFoot, FormAside, PRIMARY } from '../../../components/v2/move/FormFrame';
import { CA } from '../../../components/v2/custody-admin/paths';

const remedyOf = (err: unknown): string | null => {
  const remedy = (err as { remedy?: unknown } | null)?.remedy;
  return typeof remedy === 'string' && remedy ? remedy : null;
};

/** What an account's key is for, said: "Hot", "Treasury" — "customer deposits" for account 1. */
function jobOf(account: number | null): string | null {
  const cls = account === null ? undefined : KEY_ACCOUNT_CLASS[account];
  if (cls === undefined) return null;
  if (cls === 'deposit') return 'customer deposits';
  return CLASS_WORD[cls] ?? cls;
}
/** The purpose, with the Nano that holds it (22 Oct 2026). */
function purposeLabel(account: number, taken: boolean): string {
  const cls = KEY_ACCOUNT_CLASS[account];
  const word = CLASS_WORD[cls ?? ''] ?? `Account ${account + 1}`;
  const nano = cls === 'treasury' ? "owner's Nano" : 'desk Nano';
  return `${word} · ${nano} · account ${account + 1}${taken ? ' · registered' : ''}`;
}

function keyRefusalWords(err: unknown, account: number): { line: string; more: string } {
  const reason = err instanceof ApiError && typeof err.body.reason === 'string' ? err.body.reason : null;
  const job = jobOf(account);
  switch (reason) {
    case 'account_taken':
      return { line: `Account ${account + 1} already has an active key.`, more: `${job ? `Account ${account + 1} is ${job}'s. ` : ''}Nothing was changed. To use a different key for it, retire the old one first.` };
    case 'already_registered':
      return { line: `Account ${account + 1} is registered already — pick another.`, more: 'This key is on file. Nothing was changed; the accounts without a key are the ones to register.' };
    case 'no_registered_device':
      return { line: 'No signing device registered for you.', more: 'No key can be registered against it. Ask for your Ledger\'s address to be registered first, then try again.' };
    case 'key_not_this_ledger':
      return { line: 'That key is from another Ledger.', more: `Nothing was registered. Plug in the Ledger registered to you and export account ${account + 1} from it.` };
    default:
      return { line: 'Not registered — try again.', more: keyRefusal(err) };
  }
}

type Stage =
  | { at: 'idle' }
  | { at: 'exporting'; step: LedgerStep | null; text: string }
  | { at: 'exported'; firstAddress: string; path: string }
  | { at: 'proving'; firstAddress: string; path: string; device: string; text: string }
  | { at: 'registered'; firstAddress: string; path: string }
  | { at: 'failed'; line: string; more: string; remedy: string | null };

const STEPS = ['Connect', 'Verify device', 'Prove key'] as const;

export function RegisterKeyV2() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['custody', 'me'], queryFn: api.me });
  const keys = useQuery({ queryKey: ['custody', 'keys'], queryFn: api.keys });
  const registered: CustodyKey[] = keys.data?.keys ?? [];
  const device = me.data?.device ?? null;
  const takenOf = (a: number) => registered.some((k) => k.account === a && k.status === 'active');
  // The first account with no active key is what he came to register (22 Oct 2026); chosen once the keys are read.
  const [picked, setPicked] = useState<number | null>(null);
  const account = picked ?? KEY_ACCOUNTS.find((a) => !takenOf(a)) ?? KEY_ACCOUNTS[0] ?? 0;
  const [label, setLabel] = useState('');
  const [stage, setStage] = useState<Stage>({ at: 'idle' });
  const network = useMemo(() => buildNetwork(), []);
  const cable = webHid();

  const register = useMutation({
    mutationFn: async () => {
      if (!cable) throw new Error('This browser cannot reach a Ledger over a cable.');
      if (!network) throw new Error('This build of the app names no TRON network. Report this.');
      setStage({ at: 'exporting', step: null, text: 'Starting…' });
      const key = await exportAccountKey({ hid: cable, account, network, onStep: (step, text) => setStage({ at: 'exporting', step, text }) });
      setStage({ at: 'exported', firstAddress: key.firstAddress, path: key.path });
      let proof: { rawDataHex: string; signature: string } | undefined;
      if (key.account !== 0) {
        if (!device) throw Object.assign(new Error('No signing device is registered for you.'), { remedy: 'Ask for your Ledger address to be registered first; the proof leaves it.' });
        const at = Date.now();
        const { challenge } = await api.keyProofChallenge({ account: key.account, publicKey: key.publicKey, chainCode: key.chainCode, at });
        const built = buildKeyProof({ deviceAddress: device, firstAddress: key.firstAddress, at, challenge });
        const signed = await signOnLedger({
          hid: cable, network, signer: device, rawDataHex: built.raw_data_hex, derivation: '0/0', account: 0, prove: null,
          onStep: (_step, text) => setStage({ at: 'proving', firstAddress: key.firstAddress, path: key.path, device, text }),
        });
        proof = { rawDataHex: built.raw_data_hex, signature: signed.signature };
      }
      let answer: Awaited<ReturnType<typeof api.registerKey>>;
      try {
        answer = await api.registerKey({
          chain: 'TRC20', account: key.account, publicKey: key.publicKey, chainCode: key.chainCode,
          parentFingerprint: key.parentFingerprint, firstAddress: key.firstAddress, label: label.trim() || undefined,
          ...(proof ? { proof } : {}),
        });
      } catch (err) {
        // The account's key is registered already and it is THIS key: this browser adopts it (Astra, 22 Oct 2026).
        const reason = err instanceof ApiError && typeof err.body.reason === 'string' ? err.body.reason : null;
        const mine = registered.find((k) => k.account === key.account && k.status === 'active');
        if ((reason !== 'account_taken' && reason !== 'already_registered') || !mine || mine.firstAddress !== key.firstAddress) throw err;
        answer = { id: mine.id, account: key.account, firstAddress: key.firstAddress, lastIndex: mine.lastIndex, adopted: [], class: mine.class, classWord: mine.classWord };
      }
      rememberAccountKey(network, { account: key.account, publicKey: key.publicKey, chainCode: key.chainCode, parentFingerprint: key.parentFingerprint, firstAddress: key.firstAddress });
      setStage({ at: 'registered', firstAddress: key.firstAddress, path: key.path });
      return answer;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['custody', 'keys'] }); },
    onError: (err) => { const w = keyRefusalWords(err, account); setStage({ at: 'failed', line: w.line, more: w.more, remedy: remedyOf(err) }); },
  });
  const registeredFor = register.data?.classWord ?? (KEY_ACCOUNT_CLASS[account] === 'deposit' ? 'customer deposits' : jobOf(account));
  const proves = account !== 0;
  // Where the sequence stands: 0 Connect, 1 Verify device, 2 Prove key, 3 done.
  const at = stage.at === 'registered' ? 3 : stage.at === 'proving' ? 2 : stage.at === 'exported' ? (proves ? 2 : 1) : stage.at === 'exporting' ? (stage.step === 'show' ? 1 : 0) : 0;
  const steps = proves ? STEPS : STEPS.slice(0, 2);
  const busy = register.isPending;
  const figure = stage.at === 'registered' ? 'Registered' : stage.at === 'failed' ? 'Not registered' : busy ? STEPS[Math.min(at, 2)] : 'Required';

  return (
    <FormFrame
      title="Register device"
      aside={(
        <FormAside
          title="Device proof"
          figure={figure}
          sub="Compare the first address on the Nano."
          help={(
            <>
              <p>Ledger plugged in and unlocked, TRON app open, Ledger Live closed — Brave or Edge on the computer it is plugged into. The Nano exports the account&rsquo;s public key (nothing that can spend) and shows its first address: compare it with the one here before you confirm on the Nano.</p>
              <p>Hot, Merchant Hot and Treasury also sign, on the Nano, a 0 TRX transfer from your Ledger to the new key&rsquo;s first address — shown in full, never sendable: it expired before it was made.</p>
            </>
          )}
        />
      )}
      below={<Link to={CA.keys} className="btn-link text-meta">Devices &amp; keys</Link>}
    >
      <ol className="flex flex-wrap gap-x-4 gap-y-2" aria-label="Steps">
        {steps.map((s, i) => (
          <li key={s} aria-current={i === at ? 'step' : undefined} className={`inline-flex items-center gap-2 text-body ${i === at ? 'font-semibold' : i < at ? 'text-pos' : 'text-tg-hint'}`}>
            <span className={`grid h-6 w-6 place-items-center rounded-full border text-meta ${i < at ? 'border-pos' : 'border-stroke'}`}>{i < at ? <Check className="h-3.5 w-3.5" aria-hidden /> : i + 1}</span>
            {s}
          </li>
        ))}
      </ol>

      {stage.at === 'idle' || stage.at === 'failed' ? (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <Field label="Key purpose">
            <select className="select" value={account} onChange={(e) => setPicked(Number(e.target.value))} disabled={busy}>
              {KEY_ACCOUNTS.map((a) => <option key={a} value={a}>{purposeLabel(a, takenOf(a))}</option>)}
            </select>
          </Field>
          <Field label="Device label (optional)">
            <input className="input" value={label} placeholder="Desk Nano" onChange={(e) => setLabel(e.target.value)} disabled={busy} />
          </Field>
          <Field label="Network"><p className="py-2 text-body">TRC20</p></Field>
        </div>
      ) : null}

      {/* words:ok — the device's own step, as ledger-cable words it */}
      {stage.at === 'exporting' && <StateLine>{stage.text}</StateLine>}
      {(stage.at === 'exported' || stage.at === 'proving' || stage.at === 'registered') && (
        <div className="space-y-2 rounded-lg border border-stroke bg-panel-2 p-3">
          <p className="text-meta text-tg-hint">First address · {stage.path} — compare on the Nano</p>
          <p className="break-all font-mono text-body">{stage.firstAddress}</p>
          {stage.at === 'proving' && (
            <>
              <p className="text-meta text-tg-hint">On the Nano: 0 TRX to this address — expired, never sent.</p>
              {/* words:ok — the device's own step, as ledger-cable words it */}
              <StateLine>{stage.text}</StateLine>
            </>
          )}
          {stage.at === 'exported' && <StateLine>Recording the key…</StateLine>}
          {stage.at === 'registered' && <StateLine tone="good">Registered as the key for {registeredFor ?? `account ${account + 1}`}.</StateLine>}
        </div>
      )}
      {stage.at === 'failed' && (
        <StateLine tone="bad" more={<>{stage.more}{stage.remedy ? <span className="block">{stage.remedy}</span> : null}</>}>{stage.line}</StateLine>
      )}
      {!cable && (
        <StateLine tone="warn" more="Registering a key needs the Ledger on a cable, in a browser that can reach it: Brave or Edge on the computer it is plugged into.">
          This browser cannot reach a Ledger by cable.
        </StateLine>
      )}
      {me.isSuccess && !device && proves && stage.at === 'idle' && (
        <StateLine tone="warn">No signing device registered for you — the proof needs one.</StateLine>
      )}

      <FormFoot note={stage.at === 'registered' ? 'This browser now checks its addresses' : 'By cable · nothing exported can spend'}>
        {stage.at === 'registered' ? (
          <Link to={CA.keys} className={PRIMARY}>Done</Link>
        ) : (
          <button type="button" className={PRIMARY} disabled={busy || !cable} onClick={() => register.mutate()}>
            {busy ? 'On the Ledger…' : stage.at === 'failed' ? 'Try again' : 'Connect Ledger'}
          </button>
        )}
      </FormFoot>
    </FormFrame>
  );
}
